/**
 * The project tree pane (CLAUDE.md 14.3).
 *
 * FRILO's project file, rendered: the positions of one project, in order, each showing
 * the short code and the status an engineer scans for. The active position is marked
 * with `aria-current` rather than colour alone (14.2).
 *
 * **Order is meaningful.** Positions are numbered P01, P02 ... and those numbers reach
 * the report headers and the file names (Appendix A), so an engineer who adds a
 * position out of sequence needs to move it rather than renumber a job by hand. The
 * reorder endpoint existed from Phase A1 with nothing calling it; this is the caller.
 *
 * Two rules the interaction follows:
 *
 * - **Keyboard parity is not optional** (14.5). `Alt+Up` / `Alt+Down` on a focused row
 *   move it, matching the sheet editor's grip exactly (`sheets/components/CellRow`).
 *   Drag alone is unusable on a phone and inaccessible everywhere.
 * - **A failed reorder rolls back**, in `useReorderPositions`. A drag that appears to
 *   work and silently did not is the worst of the three outcomes.
 */
import { useState, type DragEvent, type KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import { GripVertical } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { usePositions, useProject, useReorderPositions } from '@/api/hooks'
import type { Position } from '@/api/types'
import { cn } from '@/lib/cn'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import { formatUtilisation } from '@/lib/format'

const STATUS_DOT: Record<string, string> = {
  pass: 'bg-[var(--status-pass)]',
  fail: 'bg-[var(--status-fail)]',
  stale: 'bg-[var(--status-warn)]',
  error: 'bg-[var(--status-warn)]',
  draft: 'bg-[var(--status-na)]',
  calculated: 'bg-[var(--status-na)]',
}

/** Move one id to another index, returning the new order. */
function reordered(positions: Position[], from: number, to: number): number[] {
  const ids = positions.map((position) => position.id)
  const [moved] = ids.splice(from, 1)
  if (moved === undefined) return ids
  ids.splice(to, 0, moved)
  return ids
}

export function ProjectTree({ projectId, activeId }: { projectId: number; activeId?: number }) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { data: project } = useProject(projectId)
  const { data: positions, isLoading } = usePositions(projectId)
  const reorder = useReorderPositions(projectId)

  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [overIndex, setOverIndex] = useState<number | null>(null)

  const rows = positions ?? []
  // Reordering is an edit. A reviewer or a viewer sees the list exactly as before.
  const canReorder = rows.length > 1 && (project?.capabilities?.includes('project:edit') ?? false)

  const commit = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || to >= rows.length) return
    reorder.mutate(reordered(rows, from, to), {
      onError: () => toast({ title: t('position.reorder_failed'), variant: 'error' }),
    })
  }

  const onKeyDown = (event: KeyboardEvent<HTMLElement>, index: number) => {
    if (!canReorder || !event.altKey) return
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      commit(index, index - 1)
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      commit(index, index + 1)
    }
  }

  const onDragOver = (event: DragEvent<HTMLLIElement>, index: number) => {
    if (!canReorder || dragIndex === null) return
    // Without this the drop never fires: the default action of dragover is to refuse.
    event.preventDefault()
    setOverIndex(index)
  }

  const onDrop = (index: number) => {
    if (dragIndex !== null) commit(dragIndex, index)
    setDragIndex(null)
    setOverIndex(null)
  }

  return (
    <nav className="p-2" aria-label={t('nav.projects')}>
      <Link
        to={`/projects/${projectId}`}
        className="mb-2 block truncate rounded px-2 py-2 text-sm font-semibold hover:bg-surface-raised"
      >
        {project?.name ?? <Skeleton className="h-4 w-32" />}
      </Link>

      {isLoading && (
        <div className="space-y-1 px-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      )}

      <ul className="space-y-0.5">
        {rows.map((position, index) => (
          <li
            key={position.id}
            onDragOver={(event) => onDragOver(event, index)}
            onDrop={(event) => {
              event.preventDefault()
              onDrop(index)
            }}
            className={cn(
              'flex items-center gap-1 rounded',
              dragIndex === index && 'opacity-50',
              overIndex === index && dragIndex !== null && dragIndex !== index && 'bg-surface',
            )}
          >
            {canReorder && (
              /* The grip is the drag source *and* the keyboard control, so reordering
                 never requires a pointer (14.5). Same contract as the sheet editor. */
              <button
                type="button"
                draggable
                onDragStart={() => setDragIndex(index)}
                onDragEnd={() => {
                  setDragIndex(null)
                  setOverIndex(null)
                }}
                onKeyDown={(event) => onKeyDown(event, index)}
                aria-label={t('position.reorder_grip', { name: position.name })}
                className="flex h-11 w-5 shrink-0 cursor-grab items-center justify-center text-text-muted opacity-40 hover:opacity-100 focus-visible:opacity-100 active:cursor-grabbing"
              >
                <GripVertical size={14} aria-hidden />
              </button>
            )}

            <Link
              to={`/positions/${position.id}`}
              aria-current={position.id === activeId ? 'page' : undefined}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={cn(
                'flex min-w-0 flex-1 items-center gap-2 rounded px-2 py-2 text-sm',
                position.id === activeId
                  ? 'bg-surface-raised font-medium text-text'
                  : 'text-text-muted hover:bg-surface-raised hover:text-text',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'h-2 w-2 shrink-0 rounded-full',
                  STATUS_DOT[position.status] ?? STATUS_DOT.draft,
                )}
              />
              <span className="tabular shrink-0 text-xs">
                {`P${String(position.seq).padStart(2, '0')}`}
              </span>
              <span className="min-w-0 flex-1 truncate">{position.name}</span>
              {position.result_summary?.governing_ratio != null && (
                <span className="tabular shrink-0 text-xs">
                  {formatUtilisation(position.result_summary.governing_ratio)}
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>

      {canReorder && (
        <p className="px-2 pt-2 text-xs text-text-muted">{t('position.reorder_hint')}</p>
      )}
    </nav>
  )
}
